import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { motion, useReducedMotion } from 'motion/react'
import type { Pick } from '../lib/api'
import { r4, r5, t } from '../copy'
import { Button } from '../ui/Button'
import { CategoryLabel } from '../ui/CategoryLabel'
import { List, MapIcon } from '../ui/icons'
import { spring } from '../ui/motion'
import { located } from '../lib/explore'
import { isMidnightText } from '../lib/format'

export type View = 'list' | 'map'

/** List / Map (Astra §4 segmented selector): 48px, two equal 44px targets, the indicator slides between them. */
export function ViewToggle({ view, onView, className = '' }: { view: View; onView: (v: View) => void; className?: string }) {
  const reduce = useReducedMotion() ?? false
  const opts: { v: View; label: string; Icon: typeof List }[] = [
    { v: 'list', label: r5('view.list'), Icon: List },
    { v: 'map', label: r5('view.map'), Icon: MapIcon },
  ]
  return (
    <div role="group" aria-label={r5('view.label')} className={`grid h-12 grid-cols-2 rounded-full bg-sunk p-0.5 ${className}`}>
      {opts.map(({ v, label, Icon }) => {
        const on = view === v
        return (
          <button key={v} type="button" aria-pressed={on} onClick={() => onView(v)}
            className="press relative inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full text-[15px] font-semibold text-ink">
            {on && (
              <motion.span layoutId="view-indicator" aria-hidden="true" className="absolute inset-0 rounded-full bg-surface shadow-raised ring-1 ring-line"
                transition={reduce ? { duration: 0 } : spring('selection')} />
            )}
            <span className="relative inline-flex items-center gap-1.5"><Icon size={17} />{label}</span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Phone map (Astra §3.7): full-bleed map, an opaque floating top row, and a rail of compact pick cards at the bottom.
 * Swiping the rail selects the matching pin; tapping a pin brings its card into the rail.
 */
export function PhoneMap({ rows, numbers, selected, onSelect, onView, onDetails, onNight, map, title = r4('picks.title') }: {
  title?: string
  rows: Pick[]
  numbers: Map<string, number>
  selected: string | null
  onSelect: (id: string) => void
  onView: (v: View) => void
  onDetails: (id: string) => void
  onNight: (p: Pick) => void
  map: (padding: { top: number; bottom: number; left: number; right: number }) => ReactNode
}) {
  const reduce = useReducedMotion() ?? false
  const rail = useRef<HTMLDivElement>(null)
  const bottom = useRef<HTMLDivElement>(null)
  const [railH, setRailH] = useState(220)
  const fromRail = useRef(false)
  const settle = useRef(0)

  useLayoutEffect(() => {
    const el = bottom.current
    if (!el) return
    const ro = new ResizeObserver(() => setRailH(Math.ceil(el.getBoundingClientRect().height)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // A pin tap (not a rail swipe) brings that card into the rail.
  useEffect(() => {
    if (!selected || fromRail.current) {
      fromRail.current = false
      return
    }
    const card = rail.current?.querySelector<HTMLElement>(`[data-id="${CSS.escape(selected)}"]`)
    if (card && rail.current) rail.current.scrollTo({ left: card.offsetLeft - 20, behavior: reduce ? 'auto' : 'smooth' })
  }, [selected, reduce])

  // When the rail comes to rest, the card at the leading edge is the selected one.
  const onScroll = () => {
    window.clearTimeout(settle.current)
    settle.current = window.setTimeout(() => {
      const el = rail.current
      if (!el) return
      let best: string | null = null
      let dist = Infinity
      for (const c of el.querySelectorAll<HTMLElement>('[data-id]')) {
        const d = Math.abs(c.offsetLeft - 20 - el.scrollLeft)
        if (d < dist) {
          dist = d
          best = c.dataset.id ?? null
        }
      }
      if (best && best !== selected) {
        fromRail.current = true
        onSelect(best)
      }
    }, 90)
  }

  return (
    <div className="fixed inset-0 z-40 bg-bg">
      {map({ top: 84, bottom: railH + 16, left: 32, right: 32 })}
      <div className="pointer-events-none absolute inset-x-0 top-0 pt-[calc(env(safe-area-inset-top)+12px)]">
        <div className="pointer-events-auto mx-auto flex max-w-[704px] items-center justify-between gap-3 px-4">
          <p className="t-control flex min-h-11 min-w-0 items-center rounded-[12px] bg-surface px-3 text-[15px] text-ink shadow-raised ring-1 ring-line">
            <span className="truncate">{title}</span>
          </p>
          <ViewToggle view="map" onView={onView} className="w-[164px] shrink-0 shadow-raised ring-1 ring-line" />
        </div>
      </div>
      <div ref={bottom} className="absolute inset-x-0 bottom-0 pb-[calc(env(safe-area-inset-bottom)+12px)]">
        <div ref={rail} onScroll={onScroll} className="flex snap-x snap-mandatory items-end gap-3 overflow-x-auto scroll-px-5 px-5 pb-1 pt-2 [scrollbar-width:none]">
          {rows.map((p) => {
            const n = located(p.item) ? (numbers.get(p.item.id) ?? null) : null
            const on = p.item.id === selected
            return (
              <article key={p.item.id} data-id={p.item.id}
                className={`w-[min(326px,calc(100vw-64px))] shrink-0 snap-start rounded-[20px] bg-surface p-4 shadow-floating ring-inset ${on ? 'ring-2 ring-accent' : 'ring-1 ring-line'}`}>
                <div data-cat={p.item.category} className="flex items-center gap-2">
                  {n !== null && (
                    <span aria-hidden="true" className="inline-flex size-6 items-center justify-center rounded-full bg-cat-fg text-[13px] font-semibold tabular-nums text-[var(--pin-ink)]">{n}</span>
                  )}
                  <CategoryLabel category={p.item.category} tags={p.item.tags} />
                </div>
                <h2 className="t-control mt-2 text-[17px] leading-[22px] text-ink">{p.item.title}</h2>
                <p className="t-meta mt-1 text-ink-2">
                  {[p.item.kind === 'event' && p.item.start_at ? formatWhenShort(p.item.start_at) : null, p.item.neighborhood ?? p.item.venue_name].filter(Boolean).join(' · ')}
                </p>
                {n === null && <p className="t-caption mt-1 text-ink-2">{r5('map.no_location')}</p>}
                <div className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-2">
                  <Button variant="secondary" size="sm" className="whitespace-nowrap px-3" onClick={() => onDetails(p.item.id)}>{t('pick.details')}</Button>
                  {p.night_ok === true && (
                    <Button variant="primary" size="sm" className="whitespace-nowrap px-3" onClick={() => onNight(p)}>{r5('night.button')}</Button>
                  )}
                </div>
              </article>
            )
          })}
          <span aria-hidden="true" className="w-2 shrink-0" />
        </div>
      </div>
    </div>
  )
}

const shortFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', hour: 'numeric', minute: '2-digit' })
const wkdayFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short' })
// a start at exactly midnight LA is a date without a time (B-203): the weekday only
const formatWhenShort = (iso: string) => (isMidnightText(shortFmt.format(new Date(iso))) ? wkdayFmt.format(new Date(iso)) : shortFmt.format(new Date(iso)).replace(',', ''))
